import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import SeoHead from "@/components/common/SeoHead";
import { X, Image as ImageIcon, Video } from "lucide-react";
import { getImageUrl } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";

const PLACEHOLDER_ITEMS = [
  { title: "Kegiatan komunitas Padalarang", category: "Kemanusiaan", media_url: "https://images.unsplash.com/photo-1533734635438-fa92e72a1f0e?w=1200&q=80", media_type: "image" },
  { title: "Pemberdayaan petani", category: "Ekonomi", media_url: "https://images.unsplash.com/photo-1611087802810-046bb671e644?w=1200&q=80", media_type: "image" },
  { title: "Program pertanian berkelanjutan", category: "Pangan", media_url: "https://images.pexels.com/photos/15773044/pexels-photo-15773044.jpeg?w=1200", media_type: "image" },
  { title: "Pendidikan anak Nusantara", category: "Pendidikan", media_url: "https://images.unsplash.com/photo-1549431971-f54b3f5c82d8?w=1200&q=80", media_type: "image" },
  { title: "Kegiatan lapangan", category: "Kemanusiaan", media_url: "https://images.unsplash.com/photo-1747778148316-4a8eeb132d48?w=1200&q=80", media_type: "image" },
  { title: "Konservasi lingkungan", category: "Lingkungan", media_url: "https://images.pexels.com/photos/17565235/pexels-photo-17565235.png?w=1200", media_type: "image" },
  { title: "Inovasi teknologi komunitas", category: "Inovasi", media_url: "https://images.unsplash.com/photo-1665664660924-255a6167f498?w=1200&q=80", media_type: "image" },
  { title: "Program kesehatan lansia", category: "Kesehatan", media_url: "https://images.unsplash.com/photo-1526795443948-005b48ce4791?w=1200&q=80", media_type: "image" },
];

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("Semua");
  const [type, setType] = useState("all");
  const [lightbox, setLightbox] = useState(null);
  const { settings } = useSettings();
  const pageData = settings?.gallery_page || {};

  useEffect(() => {
    api.get("/gallery", { params: { limit: 100 } })
      .then((r) => setItems(r.data.items || []))
      .catch(() => {});
  }, []);

  const displayItems = items.length > 0 ? items : PLACEHOLDER_ITEMS;
  const categories = ["Semua", ...new Set(displayItems.map((i) => i.category))];
  const filtered = displayItems.filter((i) => {
    if (filter !== "Semua" && i.category !== filter) return false;
    if (type !== "all" && i.media_type !== type) return false;
    return true;
  });

  return (
    <div data-testid="page-gallery">
      <SeoHead title={{ id: "Galeri", en: "Gallery" }} description={{ id: pageData.description || "Kumpulan foto dan video kegiatan Sauyunan Foundation.", en: "Photo and video collection of Sauyunan Foundation activities." }} />
      <PageHeader
        eyebrow={pageData.eyebrow || "Galeri"}
        title={pageData.title || "Momen bersama komunitas"}
        description={pageData.description || "Kumpulan foto dan video kegiatan Sauyunan Foundation di seluruh Nusantara."}
        image={pageData.image ? getImageUrl(pageData.image) : undefined}
      />

      <section className="section-y bg-white">
        <div className="container-sf">
          {/* Filters */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-10">
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setFilter(c)}
                  data-testid={`gallery-filter-${c.toLowerCase()}`}
                  className={`px-4 py-2 rounded-full text-xs font-semibold transition ${
                    filter === c ? "bg-[var(--brand-blue)] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}
                >{c}</button>
              ))}
            </div>
            <div className="flex gap-2">
              {[{ id: "all", label: "Semua", icon: null }, { id: "image", label: "Foto", icon: ImageIcon }, { id: "video", label: "Video", icon: Video }].map((t) => (
                <button
                  key={t.id}
                  onClick={() => setType(t.id)}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold transition ${type === t.id ? "bg-[var(--brand-orange)] text-white" : "bg-white border border-slate-200 text-slate-700"}`}
                >{t.icon && <t.icon className="h-3.5 w-3.5" />}{t.label}</button>
              ))}
            </div>
          </div>

          {/* Masonry */}
          <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4 [column-fill:_balance]">
            {filtered.map((it, i) => (
              <button
                key={i}
                onClick={() => setLightbox(it)}
                className="mb-4 block w-full break-inside-avoid rounded-2xl overflow-hidden bg-slate-100 group relative"
                data-testid={`gallery-item-${i}`}
              >
                <img src={getImageUrl(it.media_url)} alt={it.title} className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/0 to-transparent opacity-0 group-hover:opacity-100 transition p-5 flex flex-col justify-end">
                  <div className="text-xs uppercase tracking-wider text-[var(--brand-orange)] font-semibold">{it.category}</div>
                  <div className="text-white text-sm font-semibold leading-tight mt-1">{it.title}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="gallery-lightbox">
          <button className="absolute top-6 right-6 h-11 w-11 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20" onClick={() => setLightbox(null)}>
            <X className="h-5 w-5" />
          </button>
          <div className="max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={getImageUrl(lightbox.media_url)} alt={lightbox.title} className="w-full max-h-[80vh] object-contain rounded-xl" />
            <div className="mt-4 text-center text-white">
              <div className="text-xs uppercase tracking-wider text-[var(--brand-orange)] font-semibold">{lightbox.category}</div>
              <div className="text-lg font-semibold">{lightbox.title}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
